import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { HardDriveDownload, Loader2, ExternalLink, CheckCircle2, XCircle, FolderCog, Save, Link2, Unlink } from "lucide-react";

// Owner-only: connect/disconnect the Google account backups upload as. Also handles the
// ?gdrive=connected|error query param that /api/settings/gdrive-oauth/callback redirects
// back to after the Google consent screen — that round trip leaves the SPA entirely (it's
// a real browser navigation to Google and back), so this is the one place that result
// surfaces once the page reloads.
function DriveConnectSetting() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [connecting, setConnecting] = React.useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["gdrive-oauth-status"],
    queryFn: async () => (await api.get("/settings/gdrive-oauth/status")).data,
  });

  React.useEffect(() => {
    const g = searchParams.get("gdrive");
    if (g === "connected") toast.success("Google Drive berhasil terhubung");
    else if (g === "error") toast.error("Gagal menghubungkan Google Drive, coba lagi");
    if (g) {
      qc.invalidateQueries({ queryKey: ["gdrive-oauth-status"] });
      navigate("/admin?tab=backup", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/settings/gdrive-oauth/start");
      window.location.href = data.auth_url; // full navigation to Google's consent screen
    } catch (e) {
      toast.error(apiErr(e) || "Gagal memulai koneksi Google Drive");
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    try {
      await api.delete("/settings/gdrive-oauth");
      toast.success("Google Drive diputuskan");
      qc.invalidateQueries({ queryKey: ["gdrive-oauth-status"] });
    } catch (e) {
      toast.error(apiErr(e) || "Gagal memutuskan koneksi");
    }
  };

  return (
    <div className="card-surface rounded-xl p-3.5 mb-3">
      <div className="flex items-center gap-1.5 text-sm font-medium mb-1"><Link2 size={15} />Akun Google Drive</div>
      <p className="text-xs text-zinc-500 mb-2.5">
        Khusus terlihat oleh Anda (Owner). File backup akan tersimpan di Drive akun ini — sama seperti kalau Anda
        upload file secara manual.
      </p>
      {isLoading ? (
        <Loader2 size={16} className="animate-spin text-zinc-500" />
      ) : data?.connected ? (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-sm text-green-400"><CheckCircle2 size={15} />Terhubung sebagai {data.email || "akun Google"}</div>
          <Button onClick={disconnect} variant="outline" size="sm" className="bg-[#18181B] border-white/10 h-8 text-xs">
            <Unlink size={13} className="mr-1" />Putuskan
          </Button>
        </div>
      ) : (
        <Button onClick={connect} disabled={connecting} className="bg-blue-600 h-10">
          {connecting ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Link2 size={16} className="mr-1" />}
          Hubungkan Google Drive
        </Button>
      )}
    </div>
  );
}

const fmtSize = (bytes) => {
  if (!bytes) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

// Owner-only: which Drive folder backups upload into. Kept out of the Backup panel's main
// body (only rendered for role === "owner") — the folder link itself isn't shown to
// Admin/Chief at all, only Owner can see or change it, per the explicit request that this
// stay private to Owner rather than shared with the rest of the owner/chief-gated tab.
function DriveFolderSetting() {
  const [value, setValue] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["gdrive-backup-folder"],
    queryFn: async () => (await api.get("/settings/gdrive-backup-folder")).data,
  });
  React.useEffect(() => { if (data) setValue(data.folder_url || ""); }, [data]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/gdrive-backup-folder", { folder_url: value });
      toast.success("Folder backup disimpan");
    } catch (e) {
      toast.error(apiErr(e) || "Gagal menyimpan folder");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-surface rounded-xl p-3.5 mb-3">
      <div className="flex items-center gap-1.5 text-sm font-medium mb-1"><FolderCog size={15} />Folder Google Drive Tujuan Backup</div>
      <p className="text-xs text-zinc-500 mb-2.5">
        Khusus terlihat oleh Anda (Owner). Buat folder di Google Drive Anda (folder biasa, tidak perlu di-share ke
        siapa pun), lalu paste link foldernya di sini.
      </p>
      <div className="flex gap-2">
        <Input value={value} disabled={isLoading} onChange={(e) => setValue(e.target.value)}
          placeholder="https://drive.google.com/drive/folders/..." className="bg-[#18181B] border-white/10 h-10" />
        <Button onClick={save} disabled={saving} className="bg-blue-600 h-10 shrink-0">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
        </Button>
      </div>
    </div>
  );
}

// Backup Sekarang + riwayat 30 backup terakhir. Data yang di-backup adalah seluruh
// koleksi bisnis (lead, dokumen, user, cabang, master data, dst) sebagai satu file
// .json.gz, diupload ke folder Google Drive yang di-set lewat DriveFolderSetting di atas
// (Owner-only). Backup harian otomatis juga jalan lewat cron VPS (POST /cron/backup)
// memakai fungsi backend yang sama — tombol ini cuma trigger manual dari sisi yang sama.
export default function BackupPanel() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: history, isLoading } = useQuery({
    queryKey: ["backup-history"],
    queryFn: async () => (await api.get("/admin/backup/history")).data,
  });
  const [running, setRunning] = React.useState(false);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/backup/run");
      toast.success(`Backup selesai — ${data.doc_count} data, ${fmtSize(data.size_bytes)}`);
      qc.invalidateQueries({ queryKey: ["backup-history"] });
    } catch (e) {
      toast.error(apiErr(e) || "Backup gagal, cek konfigurasi Google Drive di server");
    } finally {
      setRunning(false);
    }
  };

  const last = history?.[0];

  return (
    <div className="card-surface rounded-2xl p-5 mb-3">
      <h3 className="font-heading font-semibold mb-1">Backup Data</h3>
      <p className="text-sm text-zinc-500 mb-4">
        Backup seluruh data (lead, dokumen, user, cabang, master data, dll) jadi 1 file, otomatis terupload ke
        Google Drive. Berjalan otomatis tiap hari, atau klik tombol di bawah untuk backup manual kapan saja.
      </p>

      {user?.role === "owner" && <DriveConnectSetting />}
      {user?.role === "owner" && <DriveFolderSetting />}

      <div className="flex items-center justify-between card-surface rounded-xl p-3.5 mb-3">
        <div>
          <div className="text-sm font-medium">
            {last ? (last.status === "ok" ? "Backup terakhir berhasil" : "Backup terakhir gagal") : "Belum pernah backup"}
          </div>
          <p className="text-xs text-zinc-500 mt-0.5">
            {last ? new Date(last.at).toLocaleString("id-ID") : "Klik \"Backup Sekarang\" untuk backup pertama"}
          </p>
        </div>
        <Button onClick={runNow} disabled={running} className="bg-blue-600 h-10 shrink-0">
          {running ? <Loader2 size={16} className="mr-1 animate-spin" /> : <HardDriveDownload size={16} className="mr-1" />}
          Backup Sekarang
        </Button>
      </div>

      {!isLoading && (!history || history.length === 0) && (
        <p className="text-xs text-amber-400/80">
          Belum ada riwayat backup sama sekali. Kalau tombol di atas gagal terus dengan pesan "belum dikonfigurasi",
          berarti integrasi Google Drive-nya belum disambungkan di server — hubungi yang mengatur aplikasi ini.
        </p>
      )}

      {history && history.length > 0 && (
        <div className="space-y-1.5">
          <Label className="text-xs text-zinc-500 mb-1 block">Riwayat Backup</Label>
          {history.map((h) => (
            <div key={h.backup_id} className="flex items-center justify-between card-surface rounded-lg px-3 py-2.5 text-sm">
              <div className="flex items-center gap-2 min-w-0">
                {h.status === "ok"
                  ? <CheckCircle2 size={15} className="text-green-500 shrink-0" />
                  : <XCircle size={15} className="text-red-500 shrink-0" />}
                <div className="min-w-0">
                  <div className="truncate">{h.filename || (h.status === "error" ? "Backup gagal" : h.backup_id)}</div>
                  <div className="text-xs text-zinc-500">
                    {new Date(h.at).toLocaleString("id-ID")} · {h.triggered_by === "cron" ? "Otomatis harian" : `Manual oleh ${h.triggered_by}`}
                    {h.status === "ok" && ` · ${fmtSize(h.size_bytes)} · ${h.doc_count} data`}
                  </div>
                  {h.status === "error" && <div className="text-xs text-red-400 mt-0.5">{h.error}</div>}
                </div>
              </div>
              {h.drive_link && (
                <a href={h.drive_link} target="_blank" rel="noreferrer" className="shrink-0 text-zinc-400 hover:text-white p-1.5" title="Buka di Google Drive">
                  <ExternalLink size={15} />
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
